/**
 * Decides whether a react-query mutation failure is worth a Sentry error.
 *
 * WHY THIS EXISTS
 * ---------------
 * `App.tsx` installs ONE global `MutationCache.onError` handler that calls
 * `Sentry.captureException` for every failed mutation in the dashboard. That
 * handler has no way to tell "the API 500ed" from "the admin mistyped their
 * password", so an ordinary failed login has been filed as a Sentry
 * `level: error` since 2026-04-21.
 *
 * Sentry issues this closes (all `handled: yes`, all from
 * `features/auth/hooks/useSignIn.ts` → `signInWithEmailAndPassword`, across
 * BOTH prod and staging):
 *
 *   - DASHBOARD-1Q  Firebase: Error (auth/invalid-credential)      24 events, 9 users
 *   - DASHBOARD-Y   the same, and (auth/network-request-failed)     2 events, 1 user
 *   - DASHBOARD-3S  Firebase: Error (auth/network-request-failed)   1 event,  1 user
 *
 * The user is NOT left in the dark by this change: `SignInPage.tsx` already
 * renders `<SignInErrorMessage />` from the mutation's own `error`, and the
 * global toast in `App.tsx` still fires. Only the Sentry capture is skipped.
 *
 * SCOPE — deliberately narrow (see the routine ledger's D2: a capture filter is
 * only ever allowed for genuinely expected business outcomes, never for 5xx,
 * data corruption, or money paths). This matches on an explicit allowlist of
 * Firebase auth codes and nothing else, so:
 *   - an `ApiError` carrying an HTTP 500 still reaches Sentry,
 *   - a mutation that throws a plain `Error` still reaches Sentry,
 *   - `auth/too-many-requests` is deliberately NOT filtered — a burst of those
 *     is a credential-stuffing signal and is worth waking someone for.
 */

/**
 * Firebase auth codes that mean "the human at the keyboard got it wrong" or
 * "their network blinked" — both expected outcomes of a login form.
 *
 * `auth/wrong-password` and `auth/user-not-found` are the codes older Firebase
 * SDKs (and the local Auth emulator) emit for the exact user action that the
 * current SDK reports as the consolidated `auth/invalid-credential`. They are
 * listed so the filter does not silently stop working against a different SDK
 * build, not because Sentry has reported them.
 */
const EXPECTED_FIREBASE_AUTH_CODES: ReadonlySet<string> = new Set([
    'auth/invalid-credential',
    'auth/wrong-password',
    'auth/user-not-found',
    'auth/network-request-failed',
]);

/**
 * True when `error` should be sent to Sentry — i.e. it is NOT one of the
 * expected sign-in outcomes above.
 *
 * Defaults to `true` for anything it cannot positively identify, so an
 * unrecognised shape is reported rather than swallowed.
 */
export function shouldReportToSentry(error: unknown): boolean {
    if (typeof error !== 'object' || error === null) return true;

    const { code } = error as { code?: unknown };
    if (typeof code !== 'string') return true;

    return !EXPECTED_FIREBASE_AUTH_CODES.has(code);
}
